import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, Eye, EyeOff, ArrowRight, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { showFriendlyError } from '../utils/friendlyErrors';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle } = useAuth();
  const { branding, loadTenant, clearTenant } = useTenant();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  // Load tenant branding from URL param
  useEffect(() => {
    const tenantSlug = searchParams.get('tenant');
    if (tenantSlug) {
      loadTenant(tenantSlug);
    }
  }, [searchParams]);

  const isWhiteLabel = branding?.is_white_label;
  const primaryColor = branding?.primary_color || '#3b82f6';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      
      // Organization staff are not participants, they go straight to their own console.
      if (user.role === 'org_staff') {
        navigate('/app/org');
        return;
      }

      // A guardian set this account up: the young person picks their own password first.
      if (user.must_change_password) {
        navigate('/app/set-password');
        return;
      }

      // Institutional roles bypass onboarding entirely, they go straight to their dashboard
      const institutionalRoles = ['district_admin', 'superintendent', 'principal'];
      if (institutionalRoles.includes(user.role)) {
        navigate('/app/institution/superintendent');
        return;
      }

      if (user.role === 'counselor') {
        navigate('/app/pathways/counselor');
        return;
      }

      if (!user.onboarding_complete) {
        // High-school students / school-rostered students skip the welcome flow.
        const isHighSchoolStudent = user.role === 'student' && (
          user.education_level === 'high_school' ||
          user.learning_stage === 'high_school' ||
          user.organization_id ||
          user.school_id ||
          user.current_sponsor_org_id
        );
        if (!isHighSchoolStudent) {
          navigate('/app/welcome');
          return;
        }
      }
      
      const enterpriseRoles = ['super_admin', 'org_admin', 'university_career_staff', 'workforce_manager'];
      const employerRoles = ['employer_admin', 'employer_recruiter'];
      
      if (user.role === 'super_admin' && !isWhiteLabel) {
        navigate('/enterprise');
      } else if (enterpriseRoles.includes(user.role)) {
        navigate('/enterprise');
      } else if (employerRoles.includes(user.role)) {
        navigate('/app/employer');
      } else if (user.role === 'parent') {
        navigate('/app/family');
      } else if (user.learning_stage === 'middle_school' || (user.grade && user.grade >= 6 && user.grade <= 8)) {
        navigate('/ms/home');
      } else if (user.student_type === 'college' || user.welcome_archetype === 'college_student') {
        navigate('/app/college-dashboard');
      } else {
        navigate('/app/dashboard');
      }
    } catch (error) {
      showFriendlyError(error, { fallback: 'Invalid credentials' });
    } finally {
      setLoading(false);
    }
  };

  const handleExitWhiteLabel = () => {
    clearTenant();
    navigate('/login');
  };

  return (
    <div className={isWhiteLabel ? '' : 'min-h-screen bg-navy-gradient'} style={isWhiteLabel ? { minHeight: '100vh', background: `linear-gradient(135deg, ${branding.dark_bg || '#0a1628'} 0%, ${branding.secondary_color || '#1e3a5f'} 100%)` } : undefined}>
      {!isWhiteLabel && <Navbar />}
      
      {/* White-label header bar */}
      {isWhiteLabel && (
        <div className="border-b" style={{ borderColor: `${primaryColor}33`, background: `${branding.dark_bg || '#0a1628'}ee` }}>
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt={branding.app_name} className="h-10 max-w-[220px] object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: primaryColor }}>
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">{branding.app_name}</span>
                </>
              )}
            </div>
            <button onClick={handleExitWhiteLabel} className="text-xs text-slate-300 hover:text-slate-300">
              Exit tenant view
            </button>
          </div>
        </div>
      )}
      
      <div className={`${isWhiteLabel ? 'pt-16' : 'pt-32'} pb-24 px-4`}>
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            {isWhiteLabel ? (
              <>
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt={branding.app_name} className="h-24 max-w-[300px] mx-auto mb-6 object-contain" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: primaryColor }}>
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                )}
                <h1 className="font-outfit text-3xl font-semibold text-white mb-2">{branding.app_name}</h1>
                <p className="text-slate-300">{branding.tagline || 'Sign in to your account'}</p>
              </>
            ) : (
              <>
                <img 
                  src="/fp-logo.png" 
                  alt="Agentic FuturePath AI" 
                  className="w-16 h-16 rounded-2xl mx-auto mb-6"
                />
                <h1 className="font-outfit text-3xl font-semibold text-white mb-2">Welcome back</h1>
                <p className="text-slate-300">Log in to continue your journey</p>
              </>
            )}
          </div>

          <div className="glass-card p-8" style={isWhiteLabel ? { borderColor: `${primaryColor}33` } : undefined}>
            {/* Email/password login only, Google Auth removed to enforce registration + payment */}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-dark"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Link to="/forgot-password" className="text-sm hover:opacity-80" style={{ color: primaryColor }} data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="input-dark pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white z-10 cursor-pointer p-1"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full text-white"
                style={{ background: primaryColor }}
                data-testid="login-submit"
              >
                {loading ? 'Logging in...' : 'Log in'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>

            {!isWhiteLabel && (
              <div className="mt-6 text-center">
                <p className="text-slate-300">
                  Don't have an account?{' '}
                  <Link to="/pricing" className="text-blue-400 hover:text-blue-300" data-testid="signup-link">
                    Sign up
                  </Link>
                </p>
              </div>
            )}
            {isWhiteLabel && (
              <div className="mt-6 text-center">
                <p className="text-slate-300">
                  Don't have an account?{' '}
                  <Link
                    to={`/signup?tenant=${searchParams.get('tenant') || ''}`}
                    className="hover:opacity-80"
                    style={{ color: primaryColor }}
                    data-testid="signup-link"
                  >
                    Sign up
                  </Link>
                </p>
              </div>
            )}
          </div>

          {/* Powered by badge for white-label */}
          {isWhiteLabel && (
            <p className="text-center text-xs text-slate-300 mt-6">
              Powered by Agentic FuturePath AI
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
